import React from 'react'
import { AdGroupType } from './mockData'

export type MatchKey = 'broad' | 'phrase' | 'exact' | 'product'
export type BadgeType = 'auto' | 'broad' | 'phrase' | 'exact' | 'product'
export type DotStatus = 'enabled' | 'paused' | 'archived'

export const TARGET_KEYS: MatchKey[] = ['broad', 'phrase', 'exact', 'product']
export const NEGATION_KEYS: MatchKey[] = ['phrase', 'exact', 'product']
export const MT_LABEL: Record<MatchKey, string> = {
    broad: 'Broad',
    phrase: 'Phrase',
    exact: 'Exact',
    product: 'Product',
}

// Mirror canTarget / canNegate from the source prototype. The source uses
// lowercase match-type strings ("auto", "product", "broad", etc.).
export const canTarget = (matchType: string, col: MatchKey): boolean => {
    if (matchType === 'auto') return false
    if (matchType === 'product') return col === 'product'
    return col !== 'product'
}

export const canNegate = (matchType: string, col: MatchKey): boolean => {
    // Auto campaigns can add negative keywords (phrase/exact) AND negative
    // product targets.  This matches the old-UI toggleAll / header-denominator
    // logic and the Amazon platform's actual capabilities.
    // See PROMOTION_RULE_BUSINESS_LOGIC.md §6a.
    if (matchType === 'auto') return true
    if (matchType === 'product') return col === 'product'
    return col !== 'product'
}

export const eligibleTargetKeys = (matchType: string): MatchKey[] =>
    TARGET_KEYS.filter((k) => canTarget(matchType, k))

export const eligibleNegationKeys = (matchType: string): MatchKey[] =>
    NEGATION_KEYS.filter((k) => canNegate(matchType, k))

// Convert our AdGroupType enum to the badge "type" used by the source CSS.
export const badgeTypeFor = (type: AdGroupType): BadgeType => {
    const lower = type.toLowerCase()
    if (
        lower === 'auto' ||
        lower === 'broad' ||
        lower === 'phrase' ||
        lower === 'exact' ||
        lower === 'product'
    ) {
        return lower as BadgeType
    }
    if (lower === 'target') return 'product'
    return 'broad'
}

export const MatchBadge = ({ type }: { type: BadgeType }) => {
    const label = (
        {
            auto: 'AUTO',
            broad: 'BROAD',
            phrase: 'PHRASE',
            exact: 'EXACT',
            product: 'PRODUCT',
        } as const
    )[type]
    return <span className={`pm-mt-badge pm-mt-badge--${type}`}>{label}</span>
}

/** Rule-lane badge — uses the semantic src/tgt/neg accent colours. */
export type LaneType = 'src' | 'tgt' | 'neg'
export const LANE_LABEL: Record<LaneType, string> = { src: 'SRC', tgt: 'PROMO', neg: 'NEG' }
export const LANE_TITLE: Record<LaneType, string> = {
    src: 'Used as a search term source',
    tgt: 'Used as a promotion target',
    neg: 'Used as a negation target',
}

export const LaneBadge = ({ type, children }: { type: LaneType; children?: React.ReactNode }) => (
    <span className={`pm-mt-badge pm-mt-badge--${type}`}>{children ?? LANE_LABEL[type]}</span>
)

// Deterministically derive a status per id so the layout has visual variety
// without flipping on every render.
export const deriveStatus = (id: string): DotStatus => {
    const sum = id.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0)
    const mod = sum % 5
    if (mod === 3) return 'archived'
    if (mod === 4) return 'paused'
    return 'enabled'
}

// Mirror of `shortCampaign` from promotion-matrix.jsx (lines 255-260).
export const shortCampaign = (camp: string): string => {
    const m = camp.match(/^([A-Z0-9]{8,})\b\s*[-–]\s*(.+)$/)
    if (m) return m[1] + ' · ' + m[2].slice(0, 38) + (m[2].length > 38 ? '…' : '')
    return camp.length > 50 ? camp.slice(0, 48) + '…' : camp
}
